import React, { ReactNode, useEffect } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { auth0Config } from '../config/auth0-config';
import { apiService } from '../services/api';

interface Auth0ProviderWithHistoryProps {
  children: ReactNode;
}

// Component to set up API service with Auth0 token
const Auth0ApiSetup: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  useEffect(() => {
    if (isAuthenticated) {
      // Set up the API service to use Auth0 tokens with proper refresh flow
      apiService.setTokenGetter(async () => {
        try {
          // Get token silently - Auth0 SDK handles refresh tokens automatically
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: auth0Config.audience,
              scope: auth0Config.scope,
            },
            cacheMode: 'cache-only', // Use cached tokens when possible
          });
          
          return token;
        } catch (error: any) {
          console.warn('Cached token failed, attempting fresh token:', error.message);
          
          try {
            // Try to get a fresh token if cached fails
            const freshToken = await getAccessTokenSilently({
              authorizationParams: {
                audience: auth0Config.audience,
                scope: auth0Config.scope,
              },
              cacheMode: 'off', // Force fresh token only as fallback
            });
            
            return freshToken;
          } catch (refreshError: any) {
            console.error('Token refresh failed:', refreshError.message);
            
            // Check if it's a refresh token issue
            if (refreshError.message?.includes('Missing Refresh Token') || 
                refreshError.message?.includes('refresh_token')) {
              console.warn('Refresh token expired, user needs to re-authenticate');
              // Don't return empty string immediately - let the error bubble up
              // so the API service can handle the redirect
              throw new Error('REFRESH_TOKEN_EXPIRED');
            }
            
            return '';
          }
        }
      });
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  return <>{children}</>;
};

const Auth0ProviderWithHistory: React.FC<Auth0ProviderWithHistoryProps> = ({
  children,
}) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: any) => {
    // Navigate to the intended route after login, but avoid staying on /callback or /login
    const returnTo = appState?.returnTo || '/';
    const targetPath = returnTo === '/callback' || returnTo === '/login' ? '/' : returnTo;
    navigate(targetPath);
  };

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: auth0Config.scope,
      }}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <Auth0ApiSetup>{children}</Auth0ApiSetup>
    </Auth0Provider>
  );
};

export default Auth0ProviderWithHistory; 